import { useEffect, useState } from "react";
import {
  Code2,
  Download,
  Eye,
  File,
  FileDown,
  FileSpreadsheet,
  FileText,
  ImageIcon,
  Sheet,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface ArtifactVersion {
  id: string;
  url: string;
  filename: string;
  mimeType?: string;
  sizeBytes?: number;
  displayName?: string;
  createdAt: string;
}

// Tipos suportados pelo painel lateral de preview. HTML abre em iframe
// (HtmlPreviewPanel), markdown abre renderizado via ReactMarkdown
// (MarkdownPreviewPanel). ChatPage escolhe qual componente montar baseado
// no kind.
export type SidePreviewItem = { url: string; name: string; kind: 'html' | 'markdown' };

interface SandboxArtifactProps {
  filename: string;
  url: string;
  mimeType?: string;
  sizeBytes?: number;
  displayName?: string;
  onPreview?: (item: SidePreviewItem) => void;
  versions?: ArtifactVersion[];
}

function formatBytes(bytes?: number): string {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getExt(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

function isImage(mimeType: string | undefined, ext: string): boolean {
  if (mimeType?.startsWith("image/")) return true;
  return ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext);
}

function isPdf(mimeType: string | undefined, ext: string): boolean {
  return mimeType === "application/pdf" || ext === "pdf";
}

function isCsv(mimeType: string | undefined, ext: string): boolean {
  return mimeType === "text/csv" || ext === "csv";
}

function isHtml(mimeType: string | undefined, ext: string): boolean {
  return mimeType === "text/html" || ext === "html" || ext === "htm";
}

function isMarkdown(mimeType: string | undefined, ext: string): boolean {
  return mimeType === "text/markdown" || ext === "md" || ext === "markdown";
}

// Tipo de preview lateral disponivel pra esse arquivo.
// 'html' e 'markdown' abrem painel lateral via onPreview.
// Outros tipos (pdf, csv, image) abrem em Dialog modal.
function getSidePreviewKind(mimeType: string | undefined, ext: string): 'html' | 'markdown' | null {
  if (isHtml(mimeType, ext)) return 'html';
  if (isMarkdown(mimeType, ext)) return 'markdown';
  return null;
}

function getIconConfig(mimeType: string | undefined, ext: string) {
  if (isPdf(mimeType, ext)) {
    return { Icon: FileDown, className: "text-red-400" };
  }
  if (["xlsx", "xls"].includes(ext) || mimeType?.includes("spreadsheet")) {
    return { Icon: FileSpreadsheet, className: "text-green-400" };
  }
  if (["docx", "doc"].includes(ext) || mimeType?.includes("word")) {
    return { Icon: FileText, className: "text-blue-400" };
  }
  if (isMarkdown(mimeType, ext)) {
    return { Icon: FileText, className: "text-blue-400" };
  }
  if (isCsv(mimeType, ext)) {
    return { Icon: Sheet, className: "text-green-400" };
  }
  if (isImage(mimeType, ext)) {
    return { Icon: ImageIcon, className: "text-purple-400" };
  }
  if (isHtml(mimeType, ext)) {
    return { Icon: Code2, className: "text-orange-400" };
  }
  return { Icon: File, className: "text-tertiary" };
}

function cleanFilename(filename: string, displayName?: string): string {
  if (displayName) return displayName;
  return filename.replace(/^\d{10,}-/, "");
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora há pouco";
  if (min < 60) return `${min} min atrás`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h atrás`;
  const d = Math.floor(h / 24);
  return `${d}d atrás`;
}

async function downloadBlob(url: string, downloadName: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (err) {
    // Fallback: open in new tab
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function CsvPreview({ url }: { url: string }) {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => r.text())
      .then((txt) => {
        if (cancelled) return;
        const lines = txt.split(/\r?\n/).filter((l) => l.trim().length > 0).slice(0, 5);
        setRows(lines.map((l) => l.split(",")));
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) return <div className="text-sm text-red-400">Erro ao carregar CSV: {error}</div>;
  if (!rows) return <div className="text-sm text-tertiary">Carregando…</div>;
  if (rows.length === 0) return <div className="text-sm text-tertiary">CSV vazio.</div>;

  return (
    <div className="overflow-x-auto rounded border border-color">
      <table className="w-full text-sm">
        <thead className="bg-secondary">
          <tr>
            {rows[0].map((cell, i) => (
              <th key={i} className="px-3 py-2 text-left font-semibold text-primary border-b border-color">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(1).map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 border-b border-color">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OldVersionRow({
  version,
  onPreview,
}: {
  version: ArtifactVersion;
  onPreview?: (item: SidePreviewItem) => void;
}) {
  const ext = getExt(version.filename);
  const { Icon, className: iconClass } = getIconConfig(version.mimeType, ext);
  const name = cleanFilename(version.filename, version.displayName);
  const sideKind = getSidePreviewKind(version.mimeType, ext);
  const sidePreviewable = !!sideKind && !!onPreview;

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void downloadBlob(version.url, name);
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (sideKind) onPreview?.({ url: version.url, name, kind: sideKind });
  };

  return (
    <div className="flex items-center gap-2 py-1.5 text-xs">
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${iconClass}`} />
      <div className="flex-1 min-w-0">
        <div className="truncate text-secondary">
          Versão de {timeAgo(version.createdAt)}
          {version.sizeBytes !== undefined && (
            <span className="text-tertiary"> · {formatBytes(version.sizeBytes)}</span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={handleDownload}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-tertiary hover:text-primary hover:bg-hover transition-colors"
        title="Baixar esta versão"
      >
        <Download className="w-3 h-3" />
      </button>
      {sidePreviewable && (
        <button
          type="button"
          onClick={handlePreview}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-tertiary hover:text-primary hover:bg-hover transition-colors"
          title="Visualizar esta versão"
        >
          <Eye className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export function SandboxArtifact({ filename, url, mimeType, sizeBytes, displayName, onPreview, versions }: SandboxArtifactProps) {
  const ext = getExt(filename);
  const { Icon, className: iconClass } = getIconConfig(mimeType, ext);
  const name = displayName || cleanFilename(filename);
  const downloadName = cleanFilename(filename, displayName);
  const sideKind = getSidePreviewKind(mimeType, ext);
  const sidePreviewable = !!sideKind && !!onPreview;
  const canPreview = isPdf(mimeType, ext) || isCsv(mimeType, ext) || isImage(mimeType, ext) || sidePreviewable;
  const [dialogOpen, setDialogOpen] = useState(false);

  const versionCount = versions?.length ?? 1;
  const hasMultipleVersions = versionCount >= 2;
  const olderVersions = hasMultipleVersions ? versions!.slice(1) : [];

  const olderVersionsList = (
    <>
      <div className="px-2 py-1 text-[11px] font-semibold text-tertiary uppercase tracking-wide">
        Versões anteriores
      </div>
      <div className="divide-y divide-border">
        {olderVersions.map((v) => (
          <OldVersionRow key={v.id} version={v} onPreview={onPreview} />
        ))}
      </div>
    </>
  );

  const handleDownload = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    void downloadBlob(url, downloadName);
  };

  const cardInner = (
    <>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 mt-0.5 ${iconClass}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-bold text-primary truncate flex-1" title={name}>{name}</div>
            {hasMultipleVersions && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-secondary text-tertiary hover:text-primary hover:bg-hover transition-colors"
                    title={`${versionCount} versões deste arquivo`}
                  >
                    v{versionCount}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-72 p-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {olderVersionsList}
                </PopoverContent>
              </Popover>
            )}
          </div>
          {sizeBytes !== undefined && (
            <div className="text-xs text-tertiary">{formatBytes(sizeBytes)}</div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-secondary hover:bg-hover text-primary transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Baixar
        </button>

        {canPreview && (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-secondary text-primary"
            aria-hidden="true"
          >
            <Eye className="w-3.5 h-3.5" />
            Visualizar
          </span>
        )}

        {hasMultipleVersions && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-secondary hover:bg-hover text-primary transition-colors"
                title="Ver versões anteriores"
              >
                Versões anteriores
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-72 p-2"
              onClick={(e) => e.stopPropagation()}
            >
              {olderVersionsList}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );

  const cardClassName =
    "w-full h-full text-left p-3 rounded-lg bg-context shadow-sm cursor-pointer hover:bg-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const cardStyle = {
    borderWidth: 1,
    borderStyle: "solid" as const,
    borderColor: "hsl(var(--brand-primary) / 0.3)",
  };

  // Use a div with role=button so nested <button>s inside (Baixar, Versões, etc.)
  // remain valid HTML. Plain <button> nesting is forbidden by the spec and
  // causes browsers to re-parse the tree, breaking click handlers on the
  // inner buttons (this was the cause of the "Baixar" not working bug).
  const triggerCard = (handleActivate: () => void, ariaLabel: string) => (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
      className={cardClassName}
      style={cardStyle}
    >
      {cardInner}
    </div>
  );

  if (sidePreviewable && sideKind) {
    return triggerCard(
      () => onPreview?.({ url, name, kind: sideKind }),
      `Pré-visualizar ${name}`,
    );
  }

  if (canPreview) {
    return (
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {triggerCard(() => setDialogOpen(true), `Pré-visualizar ${name}`)}
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="truncate">{name}</DialogTitle>
          </DialogHeader>
          {isPdf(mimeType, ext) && (
            <iframe src={url} className="w-full rounded border border-color" style={{ height: "70vh" }} />
          )}
          {isImage(mimeType, ext) && (
            <div className="flex items-center justify-center">
              <img src={url} alt={name} className="max-w-full max-h-[70vh] rounded" />
            </div>
          )}
          {isCsv(mimeType, ext) && <CsvPreview url={url} />}
        </DialogContent>
      </Dialog>
    );
  }

  return triggerCard(() => handleDownload(), `Baixar ${name}`);
}

export default SandboxArtifact;
